/**
 * Performance monitoring utilities for the admin dashboard
 */

export interface PerformanceMetric {
  name: string;
  value: number;
  timestamp: number;
  metadata?: Record<string, any>;
}

export interface WebVitalsMetric {
  name: 'CLS' | 'FID' | 'FCP' | 'LCP' | 'TTFB';
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  timestamp: number;
}

class PerformanceMonitor {
  private metrics: PerformanceMetric[] = [];
  private observers: PerformanceObserver[] = [];
  private isEnabled: boolean = true;

  constructor() {
    this.initializeObservers();
  }

  private initializeObservers() {
    if (typeof window === 'undefined' || !('PerformanceObserver' in window)) {
      this.isEnabled = false;
      return;
    }

    try {
      const navObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'navigation') {
            const navEntry = entry as PerformanceNavigationTiming;
            this.recordMetric(
              'page-load-time',
              navEntry.loadEventEnd - navEntry.navigationStart
            );
            this.recordMetric(
              'dom-content-loaded',
              navEntry.domContentLoadedEventEnd - navEntry.navigationStart
            );
            this.recordMetric(
              'first-paint',
              navEntry.responseEnd - navEntry.navigationStart
            );
          }
        }
      });
      navObserver.observe({ entryTypes: ['navigation'] });
      this.observers.push(navObserver);

      const resourceObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'resource') {
            const resourceEntry = entry as PerformanceResourceTiming;
            this.recordMetric(
              'resource-load-time',
              resourceEntry.responseEnd - resourceEntry.startTime,
              {
                name: resourceEntry.name,
                type: resourceEntry.initiatorType,
                size: resourceEntry.transferSize,
              }
            );
          }
        }
      });
      resourceObserver.observe({ entryTypes: ['resource'] });
      this.observers.push(resourceObserver);

      const longTaskObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'longtask') {
            this.recordMetric('long-task', entry.duration, {
              startTime: entry.startTime,
            });
          }
        }
      });
      longTaskObserver.observe({ entryTypes: ['longtask'] });
      this.observers.push(longTaskObserver);

      if ('memory' in performance) {
        setInterval(() => {
          const memory = (performance as any).memory;
          this.recordMetric('memory-used', memory.usedJSHeapSize);
          this.recordMetric('memory-total', memory.totalJSHeapSize);
          this.recordMetric('memory-limit', memory.jsHeapSizeLimit);
        }, 30000); // Every 30 seconds
      }
    } catch (error) {
      console.warn('Performance monitoring initialization failed:', error);
      this.isEnabled = false;
    }
  }

  recordMetric(name: string, value: number, metadata?: Record<string, any>) {
    if (!this.isEnabled) return;

    const metric: PerformanceMetric = {
      name,
      value,
      timestamp: Date.now(),
      metadata,
    };

    this.metrics.push(metric);

    // Keep only last 1000 metrics to prevent memory leaks
    if (this.metrics.length > 1000) {
      this.metrics = this.metrics.slice(-1000);
    }

    if (name === 'long-task' && value > 50) {
      console.warn(`Long task detected: ${value}ms`);
    }
  }

  getMetrics(name?: string): PerformanceMetric[] {
    if (name) {
      return this.metrics.filter((metric) => metric.name === name);
    }
    return [...this.metrics];
  }

  getAverageMetric(name: string, timeWindow?: number): number | null {
    const now = Date.now();
    const windowStart = timeWindow ? now - timeWindow : 0;

    const relevantMetrics = this.metrics.filter(
      (metric) => metric.name === name && metric.timestamp >= windowStart
    );

    if (relevantMetrics.length === 0) return null;

    const sum = relevantMetrics.reduce((acc, metric) => acc + metric.value, 0);
    return sum / relevantMetrics.length;
  }

  measureFunction<T>(name: string, fn: () => T): T {
    const start = performance.now();
    try {
      const result = fn();
      const duration = performance.now() - start;
      this.recordMetric(`function-${name}`, duration);
      return result;
    } catch (error) {
      const duration = performance.now() - start;
      this.recordMetric(`function-${name}-error`, duration);
      throw error;
    }
  }

  async measureAsyncFunction<T>(
    name: string,
    fn: () => Promise<T>
  ): Promise<T> {
    const start = performance.now();
    try {
      const result = await fn();
      const duration = performance.now() - start;
      this.recordMetric(`async-function-${name}`, duration);
      return result;
    } catch (error) {
      const duration = performance.now() - start;
      this.recordMetric(`async-function-${name}-error`, duration);
      throw error;
    }
  }

  startMeasurement(name: string): () => void {
    const start = performance.now();
    return () => {
      const duration = performance.now() - start;
      this.recordMetric(name, duration);
    };
  }

  exportMetrics(): string {
    return JSON.stringify(
      {
        timestamp: Date.now(),
        userAgent: navigator.userAgent,
        metrics: this.metrics,
      },
      null,
      2
    );
  }

  clearMetrics() {
    this.metrics = [];
  }

  destroy() {
    this.observers.forEach((observer) => observer.disconnect());
    this.observers = [];
    this.metrics = [];
  }
}

export const performanceMonitor = new PerformanceMonitor();

export const initWebVitals = async () => {
  try {
    const { getCLS, getFID, getFCP, getLCP, getTTFB } =
      await import('web-vitals');

    const sendToAnalytics = (metric: WebVitalsMetric) => {
      performanceMonitor.recordMetric(
        `web-vitals-${metric.name.toLowerCase()}`,
        metric.value,
        {
          rating: metric.rating,
        }
      );

      if (metric.rating === 'poor') {
        console.warn(
          `Poor Web Vital detected: ${metric.name} = ${metric.value}`
        );
      }
    };

    getCLS(sendToAnalytics);
    getFID(sendToAnalytics);
    getFCP(sendToAnalytics);
    getLCP(sendToAnalytics);
    getTTFB(sendToAnalytics);
  } catch (error) {
    console.warn('Web Vitals not available:', error);
  }
};

export const usePerformanceMetric = (name: string) => {
  const startMeasurement = () => performanceMonitor.startMeasurement(name);
  const recordMetric = (value: number, metadata?: Record<string, any>) =>
    performanceMonitor.recordMetric(name, value, metadata);

  return { startMeasurement, recordMetric };
};

export const analyzeBundleSize = () => {
  if (typeof window === 'undefined') return;

  const scripts = Array.from(document.querySelectorAll('script[src]'));
  const stylesheets = Array.from(
    document.querySelectorAll('link[rel="stylesheet"]')
  );

  const resources = [...scripts, ...stylesheets]
    .map((element) => {
      const src = element.getAttribute('src') || element.getAttribute('href');
      return src;
    })
    .filter(Boolean);

  console.group('Bundle Analysis');
  console.log('Total resources:', resources.length);
  console.log('Resources:', resources);
  console.groupEnd();

  return resources;
};

export const checkPerformanceBudget = () => {
  const budgets = {
    'page-load-time': 3000, // 3 seconds
    'dom-content-loaded': 1500, // 1.5 seconds
    'long-task': 50, // 50ms
    'memory-used': 50 * 1024 * 1024, // 50MB
  };

  const violations: string[] = [];

  Object.entries(budgets).forEach(([metricName, budget]) => {
    const average = performanceMonitor.getAverageMetric(metricName, 60000); // Last minute
    if (average && average > budget) {
      violations.push(`${metricName}: ${average.toFixed(2)} > ${budget}`);
    }
  });

  if (violations.length > 0) {
    console.warn('Performance budget violations:', violations);
  }

  return violations;
};
